import { Site, Address, Entity, PhotoSettings, Company, InvoiceContact, SiteIntegrationMapping, EntitySubStatus, SearchableEntity } from '..';
import { ActivityTag, ComplianceDocument, TimesheetEntry, Flag } from '../';
import { Location } from '../shared';
import { ActiveWorkOrderResource } from '../workorder.resources';

export enum ContactType {
    ServiceProvider = 'Service Provider',
    Business = 'Business',
    Individual = 'Individual',
    Supplier = 'Supplier',
    Employee = 'Employee',
    Subcontractor = 'Subcontractor',
    LoginContact = 'Login Contact',
    BasicContact = 'Basic Contact',
    SystemAdministrator = 'System Administrator',
    System = 'System',
    Contact = 'Contact',
    WorkBuddyCompany = 'WorkBuddyCompany',
    Equipment = 'Equipment',
    Run = 'Run',
    External = 'External',
    Site = 'Site',
    Adhoc = 'Adhoc',
}

export const AccountTypes = [
    ContactType.ServiceProvider,
    ContactType.Business,
    ContactType.Individual,
    ContactType.Subcontractor,
    ContactType.Supplier,
    ContactType.WorkBuddyCompany,
    ContactType.Site
];

export const CustomerAccountTypes = [
    ContactType.ServiceProvider,
    ContactType.Business,
    ContactType.Individual,
];

export const SupplierAccountTypes = [
    ContactType.Supplier,
    ContactType.Subcontractor,
];

export const ContactTypes = [
    ContactType.Employee,
    ContactType.LoginContact,
    ContactType.BasicContact
];

export const CompanyTypes = [
    ContactType.WorkBuddyCompany,
];

export enum CreditAccountType {
    Prepaid = 'Prepaid',
    Credit = 'Credit'
}

export enum CreditLimitOption {
    Warning = 'Warning',
    Block = 'Block'
}

export enum DueDateType {
    DueOnReceipt = 'Due on Receipt',
    XDayAfterTheBillInvoiceDate = 'X day(s) after the bill/invoice date',
    XDayAfterEndOfBillInvoiceMonth = 'X day(s) after end of bill/invoice month',
    XDayCurrentMonth = 'Xth day of the current month',
    XDayFollowingMonth = 'Xth day of the following month'
}

export enum ContactGroup {
    Customer = 'Customer',
    Supplier = 'Supplier',
    Invited = 'Invited',
    User = 'User'
}

export enum ContactCounterName {
    ServiceProvider = 'Service Provider',
    Business = 'Business',
    Individual = 'Individual',
    Supplier = 'Supplier',
    Employee = 'Employee',
    Subcontractor = 'Subcontractor',
    Invited = 'Invited'
}

export enum SubContactType {
    Job = 'Job',
    Billing = 'Billing'
}

export enum InviteType {
    Invalid = 'Invalid',
    Valid = 'Valid',
    Connect = 'Connect',
    Enable = 'Enable',
    Invite = 'Invite',
    Resend = 'Resend',
    EnableLogin = 'Enable Login',
    None = 'None'
}

export enum AccessType {
    Employee = 'Employee',
    Subcontractor = 'Subcontractor',
    Client = 'Client'
}

export enum ContactDialogType {
    Edit = 'Edit',
    Add = 'Add',
    Site = 'Site',
    AddBasic = 'Add Basic',
    AddLogin = 'Add Login',
    EditChildContact = 'Edit Child Contact',
    None = 'None',
    InviteSuccess = 'Invite Success',
    ConnectSuccess = 'Connect Success'
}

export enum ContactFilterType {
    AllCustomers = 'All Customers',
    ServiceProvider = 'Service Providers',
    Businesses = 'Businesses',
    Residential = 'Residential',
    Suppliers = 'Suppliers',
    Companies = 'Companies',
    Contractors = 'Contractors',
    AllEmployees = 'All Employees',
    ActiveEmployees = 'Active Employees',
    InvitedEmployees = 'Invited Employees',
    AllContacts = 'All Contacts',
    AllUsers = 'All Users',
    ActiveUsers = 'Active Users',
    InvitedUsers = 'Invited Users',
    Sites = 'Sites',
}

export enum CrmCounterName {
    AllCustomers = 'All Customers',
    ServiceProvider = 'Service Providers',
    Businesses = 'Businesses',
    Residential = 'Residential',
    Suppliers = 'Suppliers',
    Companies = 'Companies',
    Contractors = 'All Contractors',
    Active = 'Active',
    Invited = 'Invited',
    Contacts = 'Contacts',
    Sites = 'Sites'
}


export interface InvoiceOptions {
    enabled: boolean;
    email?: string;
    emailCC?: string[];
    address?: Address;
}

export interface SchedulingOptions {
    enabled: boolean;
    address?: Address;
    timezone?: string;
    tags?: Entity[];
}

export interface LoginOptions {
    invite: boolean;
    lastLogin?: Date;
    lastSeen?: Date;
    devices?: string[];
    system?: boolean;
    locked?: boolean;
    showWizard?: boolean;
}

export interface DispatchOptions {
    enabled?: boolean;
    email?: string;
    sendJsonFile?: boolean;
    sendPDFFile?: boolean;
    autoDispatch?: boolean;
}

export interface AccountingOptions {
    salesTaxRate?: Entity;
    purchaseTaxRate?: Entity;
    markupPercent?: number;
}

export interface ContactLocation {
    date: Date;
    location: Location;
}

export interface ParentContact extends Entity {
    type: ContactType;
    role?: Entity;
    login?: LoginOptions;
    primary?: boolean;
    companies?: Entity[];
}

export interface PrimaryContact extends Entity {
    firstName?: string;
    lastName?: string;
    title?: string;
    phone?: string;
    mobile?: string;
    email?: string;
    note?: string;
}

export interface SocialNetwork {
    facebook?: string;
    linkedin?: string;
    instagram?: string;
    twitter?: string;
}

export interface DueDateOptions {
    value: number;
    type?: DueDateType;
    disabled?: boolean;
    min?: number;
    max?: number;
}

export interface CreditOptions {
    type: CreditAccountType;
    limitAmount?: number;
    limitBlock?: Entity;
    dueDate?: DueDateOptions;
    enforceOverdue?: boolean;
}

export interface BillToContact extends Entity {
    mobile?: string;
    email?: string;
}

export interface BillToAccount extends Entity {
    legalName?: string;
    phone?: string;
    email?: string;
    credit?: CreditOptions;
}

export interface BillToOptions {
    company?: BillToAccount;
    site?: Site;
    contact?: BillToContact;
}

export interface WorkBuddyConnectOptions {
    _id: string;
    enabled: boolean;
    restrictJobTypes?: Entity[];
}

export interface ContactEntity extends Entity {
    type: ContactType;
}

export interface LoginContactEntity extends ContactEntity {
    dispatching?: DispatchOptions;
}

export interface SmsContactEntity extends ContactEntity {
    mobile?: string;
}

export interface EmailContactEntity extends ContactEntity {
    email?: string;
}

export interface NotifiableContactEntity extends SmsContactEntity, EmailContactEntity { }

// Please only use in Job - This was created becasue the whole contact was incorreclty being stored in supervisor field
export interface SupervisorEntity extends Entity {
    type: ContactType;
    mobile: string;
    phone: string;
    email: string;
}

export interface CreditAmount {
    overdue: number;
    outstanding: number;
}

export interface TimesheetOptions {
    workRole?: Entity;
    hourlyCostRate?: number;
}

export interface Contact extends ContactEntity, SmsContactEntity, EmailContactEntity, SearchableEntity {
    legalName?: string;
    type: ContactType;
    subType?: SubContactType;
    firstName?: string;
    lastName?: string;
    businessName?: string;
    phone?: string;
    mobile?: string;
    workPhone?: string;
    email: string;
    username?: string;
    address?: Address;
    contact?: PrimaryContact;
    owner?: Entity;
    primary?: boolean;
    taxId?: string;
    stockLocation?: boolean;

    /**
     * @deprecated
     * Sites are now stored in the sites collection
     */
    sites?: Site[];
    parents?: ParentContact[];
    role?: Entity;
    labels?: Entity[];
    workCategories?: Entity[];
    companies?: Entity[];
    scheduling?: SchedulingOptions;
    dispatching?: DispatchOptions;
    invoices?: InvoiceOptions;
    bills?: InvoiceOptions;
    accounting?: AccountingOptions;
    createdBy?: Entity;
    createdAt?: Date;
    updatedAt?: Date;
    updatedBy?: Entity;
    hidePrices?: boolean;
    pricebook?: Entity;
    tags?: ActivityTag[];
    complianceExpiry?: Date;
    newComplianceAdded?: Date;
    password?: string;
    login?: LoginOptions;
    billingAddress?: Address;
    shippingAddress?: Address;
    recentLocation?: ContactLocation;
    recentLocations?: ContactLocation[];
    feedUserId?: string;
    website?: string;
    description?: string;
    title?: string;
    primarySite?: Site;
    credit?: CreditOptions;
    billTo?: BillToOptions;
    financeTags?: Entity[];
    customFields?: any;
    socialNetwork?: SocialNetwork;
    activeWorkOrder?: ActiveWorkOrderResource;
    fieldImageSettings?: ContactPhotoSettings;
    creditAmount?: CreditAmount;
    timesheet?: TimesheetOptions;
    /** @description credit limit block */
    isBlock?: boolean;
    annualRevenue?: number;
    industry?: string;
    employees?: number;
    zones?: Entity[];
    importId?: string;

    invoiceTheme?: Entity;
    bulkInvoiceTheme?: Entity;
    billTheme?: Entity;
    allowOnlinePayment?: boolean;

    integrationId?: string;
    alternativeEmail?: string; //// This email is using to send password and resend password

    note?: string;

    /** @description job templates */
    defaultServices?: Entity[];

    siteIntegrationMapping?: SiteIntegrationMapping;

    status?: EntitySubStatus;
    emailReminder?: Flag;

    subscriptionTags?: Entity[];
    connectOptions?: WorkBuddyConnectOptions;

    /** @description Settings for type=ServiceProvider only */
    enableSelectAnySite?: boolean;

    /** @description Defines the number of past financial years (1 Jul - 30 Jun) for which jobs will be retrieved. Based on created date of the job */
    clientPortalFinancialYears?: number;
}

export interface ContactSummary extends Entity {
    username?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    mobile?: string;
    workPhone?: string;
    email?: string;
    address?: Address;
    type?: ContactType;
    active?: boolean;
    disabled?: boolean;
    complianceExpiry?: Date;
    subType?: SubContactType;
    scheduling?: SchedulingOptions;
    dispatching?: DispatchOptions;
    billingAddress?: Address;
    bills?: InvoiceOptions;
    billingContacts?: ContactSummary[];
    recentLocation?: ContactLocation;
    timezone?: string;
    //sites?: Site[];
    labels?: any[];
    workCategories?: Entity[];
    title?: string;
    primarySite?: Site;
    accounts?: Entity[];
    parents?: ParentContact[];
    contact?: PrimaryContact;
    billTo?: BillToOptions;
    fieldImageSettings?: ContactPhotoSettings;
    zones?: Entity[];

    invoiceTheme?: Entity;
    billTheme?: Entity;
    accounting?: AccountingOptions;

    invoicingContact?: InvoiceContact;
    credit?: CreditOptions;

    note?: string;
    firstParent?: Entity;
    companies?: Entity[];
}

export interface ContactGroupedSummary {
    _id: string;
    type?: ContactType;
    name: string;
    phone?: string;
    mobile?: string;
    workPhone?: string;
    email?: string;
    parent?: Entity;
}


export interface CustomerPicker extends ContactSummary {
    salesTaxRate?: Entity;
}

export interface SupplierPicker extends ContactSummary {
    purchaseTaxRate?: Entity;
    markupPercent?: number;
}

export interface SubcontractorPicker extends ContactSummary {
    purchaseTaxRate?: Entity;
}

export interface BillAccount extends ContactSummary {
    purchaseTaxRate?: Entity;
}



export interface ContactWithCompliance extends Contact {
    compliances: ComplianceDocument[];
}

export interface ContactWithTimesheets extends Contact {
    timesheets: TimesheetEntry[];
}

export interface ContactInfo {
    _id?: string;
    name?: string;
    address: Address;
    phone: string;
    email: string;
    contactName: string;
    originalAccount?: Entity;
    friendlyName?: string;
}

export interface ContactPhotoSettings {
    overrideGlobalSettings: boolean;
    customPhotoSettings: PhotoSettings;
}

export interface RecommendedContactSummary extends ContactSummary {
    group: 'Recommended' | 'Partially Recommended' | string;
    groupOrder: number;
}

export enum AccountUploadField {
    Type = 'Type',
    AccountName = 'Name',
    LegalName = 'Legal Name',
    TaxID = 'TaxId',
    Phone = 'Phone',
    Email = 'Email',
    Website = 'Website',
    Notes = 'Notes',
    PrimaryContactFirstName = 'Primary Contact First Name',
    PrimaryContactLastName = 'Primary Contact Last Name',
    PrimaryContactMobile = 'Primary Contact Mobile',
    PrimaryContactEmail = 'Primary Contact Email',
    PrimaryContactJobTitle = 'Primary Contact Job Title',
    PrimarySiteStreet = 'Primary Site Street',
    PrimarySiteSuburb = 'Primary Site Suburb',
    PrimarySiteState = 'Primary Site State',
    PrimarySitePostCode = 'Primary Site Post Code'
}

export enum SiteUploadField {
    Name = 'name',
    Street = 'street',
    Suburb = 'suburb',
    State = 'state',
    Postcode = 'postcode',
    ContactFirstName = 'contactFirstName',
    ContactLastName = 'contactLastName',
    ContactMobile = 'contactMobile',
    ContactEmail = 'contactEmail'
}

export const pendingInviteKey: string = 'pendingInvite';

export function getAccountFromCompany(company: Company): Contact {

    const primarySite = {
        _id: company._id,
        name: 'Primary Site',
        system: true
    };

    const contact: Contact = {
        _id: company._id,
        type: ContactType.WorkBuddyCompany,
        name: company.name,
        legalName: company?.legalName ?? company.name,
        phone: company?.phone,
        email: company?.email,
        website: company?.website,
        system: true,
        contact: {
            _id: company._id,
            name: 'Primary Contact',
            firstName: 'Primary ',
            lastName: 'Contact',
            phone: company?.phone,
            email: company?.email,
            system: true
        },
        sites: [
            {
                ...primarySite,
                address: company.address,
                phone: company.phone,
                website: company.website,
            } as Site
        ],
        billTo: {
            company: {
                _id: company._id,
                name: company.name,
            },
            contact: {
                _id: company._id,
                name: `Primary Contact`
            },
            site: {
                ...primarySite
            } as Site
        },
        primarySite: primarySite as Site,
        emailReminder: null,
    };

    return contact;
}

export function getContactFromCompany(company: Company): Contact {
    return {
        _id: company._id,
        type: ContactType.WorkBuddyCompany,
        name: company.name,
        legalName: company.legalName,
        phone: company.phone,
        email: company.email,
        address: company.address,
        website: company.website,
        contact: {
            _id: null,
            type: ContactType.BasicContact,
            firstName: 'Primary',
            lastName: 'Contact',
            phone: company.phone,
            email: company.email,
        } as any,
    }
}

export type ContactCommands =
    {
        commandId: 'ResendInvite',
        data: {
            contact: Contact;
            parentId: string;
        }
    } |
    {
        commandId: 'DeleteInvite',
        data: {
            contactId: string;
            parentId: string;
        }

    };
